import React from 'react';
import { useTranslation } from 'react-i18next';
import { SelectField, TextButton } from '@oneaudi/unified-web-components';
import useFetchDealersByMarket, {
  type Dealer,
} from '../../hooks/fetchHooks/useFetchDealersByMarket';
import { DropDownWrapper } from './ServiceSchedule.styles';

const ServiceSchedule = () => {
  const { t, i18n } = useTranslation();
  const { uniqueRegions, getDealersByRegion, loading } = useFetchDealersByMarket();

  const isFrench = i18n.language?.toLowerCase().startsWith('fr');

  const [province, setProvince] = React.useState<string>('');
  const [dealerSelected, setDealerSelected] = React.useState<Dealer | null>(null);

  // 1. Derive dealers directly during render. No useEffect needed!
  const dealers = React.useMemo(() => {
    return province ? getDealersByRegion(province) : [];
  }, [province, getDealersByRegion]);

  const onHandleProvinceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setProvince(e.target.value);
    // 2. Clear the selected dealer when the user switches to a new province
    setDealerSelected(null);
  };

  const onHandleDealerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedDealer = dealers.find((dealer) => dealer.name === e.target.value) || null;
    setDealerSelected(selectedDealer);
  };

  return (
    <div>
      <DropDownWrapper>
        <SelectField
          label={t('digitalLeads.inputs.schedServiceProvince')}
          id="dropdown-selector"
          onChange={onHandleProvinceChange}
          disabled={loading}
          value={province}
        >
          <option key="default" data-testid="dropdown-option-checkbox-default" value="">
            {' '}
          </option>
          {uniqueRegions.map((option) => {
            return (
              <option
                key={option}
                data-testid={`dropdown-option-checkbox-${option}`}
                value={option}
              >
                {option}
              </option>
            );
          })}
        </SelectField>
      </DropDownWrapper>

      {province && (
        <DropDownWrapper>
          <SelectField
            label={t('digitalLeads.inputs.schedServiceDealer')}
            hint={t('digitalLeads.inputs.schedServiceDealerHint')}
            id="dropdown-selector"
            margin={['24px', 0, '48px', 0]}
            onChange={onHandleDealerChange}
            disabled={!province}
            value={dealerSelected?.name || ''}
          >
            <option key="default" data-testid="dropdown-option-checkbox-default" value="">
              {' '}
            </option>
            {dealers.map((option) => {
              const { name } = option;
              return (
                <option key={name} data-testid={`dropdown-option-checkbox-${name}`} value={name}>
                  {name}
                </option>
              );
            })}
          </SelectField>
        </DropDownWrapper>
      )}

      {dealerSelected && (
        <TextButton
          href={isFrench ? dealerSelected.requestServiceFr : dealerSelected.requestServiceEn}
          isFullWidth
          variant="primary"
        >
          {t('digitalLeads.inputs.schedServiceButton')}
        </TextButton>
      )}
    </div>
  );
};

export default ServiceSchedule;
